import { motion } from "framer-motion";
import { Reveal } from "@/components/motion/Reveal";
import EarlyAccessButton from "@/components/EarlyAccessButton";
import EarlyAccessForm from "@/components/EarlyAccessForm";

const NOTES = [
  { tier: "Top", label: "Ambrette", sub: "warm skin", mark: "01" },
  { tier: "Heart", label: "Orris", sub: "dry powder", mark: "02" },
  { tier: "Base", label: "Amber", sub: "low warmth", mark: "03" },
];

export default function Fragrance() {
  return (
    <div data-testid="fragrance-page">
      {/* Hero */}
      <section data-testid="fragrance-hero" className="relative bg-ink text-ivory overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_underbloom-launch/artifacts/qrbdb3su_Underbloom_Ingredients_Wide.png"
          alt="Whisper — the fragrance ingredients"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-ink/30" />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-ink/70 to-transparent" />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 min-h-[85vh] flex flex-col justify-end pt-40 pb-20">
          <p className="eyebrow mb-6" style={{ color: "#AFA268" }}>The Fragrance Spine</p>
          <h1 className="font-display leading-[1.14]" style={{ fontSize: "clamp(64px, 12vw, 200px)", fontWeight: 300 }}>
            <span className="block reveal-mask">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="inline-block"
              >
                It begins with
              </motion.span>
            </span>
            <span className="block reveal-mask">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay: 0.42, ease: [0.22, 1, 0.36, 1] }}
                className="inline-block italic"
              >
                Whisper.
              </motion.span>
            </span>
          </h1>
          <p className="mt-8 font-display italic text-3xl md:text-4xl text-ivory/80">Dry Skin-Musk Vanilla</p>
        </div>
      </section>

      {/* The Scent */}
      <section data-testid="fragrance-scent" className="bg-ivory">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <Reveal>
              <p className="font-mono-stamp text-pencil">Chapter I</p>
              <p className="eyebrow eyebrow-brass mt-3">The Scent</p>
            </Reveal>
          </div>
          <div className="md:col-span-9">
            <Reveal>
              <p className="font-display text-3xl md:text-5xl leading-[1.32]" style={{ fontWeight: 300 }}>
                One scent, made properly. Whisper opens the way warm skin does, a soft breath of ambrette, barely there. It settles into orris, dry and powdery, the quiet luxury of clean skin rather than perfume. Underneath, a low amber warmth holds close and stays, never rising, never announcing itself. It is not a fragrance you wear over your day. It is the one you <em className="italic">forget you have on</em> until someone stands close enough to notice.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Notes card */}
      <section data-testid="fragrance-notes" className="bg-cream">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-4">
              <p className="font-mono-stamp text-pencil">Chapter II</p>
              <p className="eyebrow eyebrow-brass mt-3">The Notes</p>
              <h3 className="font-display text-5xl md:text-6xl mt-8 leading-[1.12]" style={{ fontWeight: 300 }}>
                Three tiers, <em className="italic">one line.</em>
              </h3>
            </div>
            <div className="md:col-span-7 md:col-start-6">
              <div className="grid grid-cols-3 gap-3 md:gap-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_underbloom-launch/artifacts/4sam8qs1_ChatGPT%20Image%20Jul%2010%2C%202026%2C%2003_42_32%20AM.png"
                  alt="Amber"
                  className="w-full h-[280px] md:h-[420px] object-cover border border-ink/10"
                  loading="lazy"
                />
                <img
                  src="https://customer-assets.emergentagent.com/job_underbloom-launch/artifacts/778i0pb9_ChatGPT%20Image%20Jul%2010%2C%202026%2C%2003_42_37%20AM.png"
                  alt="Orris"
                  className="w-full h-[280px] md:h-[420px] object-cover border border-ink/10"
                  loading="lazy"
                />
                <img
                  src="https://customer-assets.emergentagent.com/job_underbloom-launch/artifacts/06x12g14_ChatGPT%20Image%20Jul%2010%2C%202026%2C%2003_43_48%20AM.png"
                  alt="Ambrette"
                  className="w-full h-[280px] md:h-[420px] object-cover border border-ink/10"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-ink">
            {NOTES.map((n, i) => (
              <Reveal key={n.tier} delay={i * 0.1}>
                <div className={`p-8 md:p-10 min-h-[280px] flex flex-col justify-between border-b md:border-b-0 border-ink ${i < 2 ? "md:border-r" : ""}`}>
                  <div className="flex justify-between items-start">
                    <p className="font-mono-stamp text-pencil">{n.mark}</p>
                    <p className="eyebrow eyebrow-brass">{n.tier}</p>
                  </div>
                  <div>
                    <p className="font-display text-6xl md:text-7xl leading-[1.12]" style={{ fontWeight: 300 }}>
                      {n.label}
                    </p>
                    <p className="font-display italic text-2xl text-pencil mt-3">{n.sub}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="font-mono-stamp text-pencil mt-6 md:mt-8 text-right">
            Preliminary composition · Subject to change
          </p>
        </div>
      </section>
      <section data-testid="fragrance-spine" className="bg-ivory">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="font-mono-stamp text-pencil">Chapter III</p>
            <p className="eyebrow eyebrow-brass mt-3">Why One Scent</p>
            <h3 className="font-display text-5xl md:text-7xl mt-8 leading-[1.12]" style={{ fontWeight: 300 }}>
              A spine, <em className="italic">not a shelf.</em>
            </h3>
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-8">
            <Reveal>
              <p className="text-[16px] leading-[1.8]">
                Most houses open with a fragrance library and hope one lands. We began with one. Whisper sets the register for the house, a single sensibility everything to come will share, so the collection reads as worn from the same hand rather than assembled.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="font-display italic text-2xl md:text-3xl leading-tight text-pencil">
                One scent to open the house. A wardrobe to follow, as the house grows.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* How to wear */}
      <section data-testid="fragrance-wear" className="bg-ink text-ivory">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="font-mono-stamp text-brass">Chapter IV</p>
            <p className="eyebrow mt-3" style={{ color: "#AFA268" }}>How to Wear</p>
            <h3 className="font-display text-5xl md:text-6xl mt-8 leading-[1.12]" style={{ fontWeight: 300 }}>
              Close, <em className="italic">never loud.</em>
            </h3>
          </div>
          <div className="md:col-span-7 md:pl-10">
            <p className="text-[16px] leading-[1.8] text-ivory/85 max-w-xl">
              Wear it under your own fragrance to warm and hold it, or wear it alone as clean, expensive skin.
              Either way it stays close — the note people lean in for, not the one that arrives before you.
            </p>
          </div>
        </div>
      </section>

      {/* Join section removed */}
    </div>
  );
}
